import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { TextInput } from '@frontend/components/basic-forms';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage, useLocale } from '@frontend/lang';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { Transaction, TransactionStateName } from '@frontend/transaction/types';
import type { Dispatch } from 'react';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { LuTriangleAlert } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import useEnterTrackingNumber from './enter-tracking-number.controller';

const ID = 'enter-tracking-number';

interface ExistingTrackingNumberMatchProps {
    transaction: Transaction;
    currentUserId: string | null;
    isContinuing: boolean;
    onContinueWithTransaction: Dispatch<Transaction>;
}

const ExistingTrackingNumberMatch = (props: ExistingTrackingNumberMatchProps) => {
    const { locale } = useLocale();
    const canContinue = props.transaction.user_id === props.currentUserId && props.transaction.state === TransactionStateName.TRANSACTION_DRAFT;
    const createdAt = props.transaction.creation_timestamp
        ? formatDateTimeWithLocale(new Date(props.transaction.creation_timestamp), locale, DateFormat.DD_MM_YYYY_HH_MM)
        : '/';

    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-3 self-stretch rounded-xl p-4 outline-1'>
            <div className='inline-flex min-h-6 items-center justify-between gap-4 self-stretch'>
                <div className='text-brand-muted shrink-0 justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}</div>
                <TransactionStateBadge state={props.transaction.state} />
            </div>
            <div className='inline-flex min-h-6 items-center justify-between gap-4 self-stretch'>
                <div className='text-brand-muted shrink-0 justify-start text-base font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT}</div>
                <div className='text-foreground-default justify-start text-base font-medium'>{createdAt}</div>
            </div>
            {canContinue && (
                <div className='flex w-full justify-end pt-1'>
                    <DefaultPrimaryButton
                        text={CommonMessage.BUTTONS.CONTINUE}
                        onClick={() => props.onContinueWithTransaction(props.transaction)}
                        disabled={props.isContinuing}
                    />
                </div>
            )}
        </div>
    );
};

const EnterTrackingNumber = () => {
    const intl = useIntl();
    const viewProps = useEnterTrackingNumber();
    const inputComplete = viewProps.trackingNumber.trim().length > 0;

    return (
        <WizardScreenContainer
            id={ID}
            header={{ title: CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER }}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled
            }}
            loadingModal={{ show: viewProps.isSubmitting || !!viewProps.continuingTransactionId }}
            containerClassName='bg-background-default flex flex-1 flex-col items-start justify-start gap-4 self-stretch overflow-y-auto px-5 pt-6 pb-20'>
            <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.PROVIDE_THE_FOLLOWING}</div>

            <div className='bg-background-surface inline-flex w-full flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='flex items-center justify-start gap-4'>
                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                            {inputComplete ? (
                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            ) : (
                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                            )}
                            <ICONS.ENTITY.TRANSACTION className='size-1/2' />
                        </div>
                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                {CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex w-full items-end gap-2'>
                    <TextInput
                        id='enter-tracking-number-input'
                        className='w-full'
                        value={viewProps.trackingNumber}
                        onChange={viewProps.setTrackingNumber}
                        isClearable
                        isLoading={viewProps.isGeneratingTrackingNumber}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !viewProps.submitDisabled) {
                                void viewProps.onSubmit();
                            }
                        }}
                    />
                    <button
                        type='button'
                        disabled={viewProps.isGeneratingTrackingNumber || viewProps.isSubmitting || !!viewProps.continuingTransactionId}
                        className='hover:bg-brand-light text-brand-primary disabled:text-brand-disabled disabled:hover:text-brand-disabled hover:text-brand-secondary inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-2.5 rounded-xl px-4 disabled:cursor-not-allowed disabled:hover:bg-inherit'
                        onClick={() => {
                            void viewProps.onGenerateTrackingNumber();
                        }}>
                        <span className='text-base font-medium'>
                            {intl.formatMessage({
                                id: 'platform.wizard.enter-tracking-number.generate',
                                description: 'Button label that generates a unique tracking number',
                                defaultMessage: 'Generate'
                            })}
                        </span>
                    </button>
                </div>

                <div className='text-brand-muted text-sm'>
                    <FormattedMessage
                        id='platform.wizard.enter-tracking-number.required'
                        description='Validation hint indicating the tracking number is required before continuing'
                        defaultMessage='Tracking number is required.'
                    />
                </div>
            </div>

            {viewProps.existingTransactions && viewProps.existingTransactions.length > 0 && (
                <div className='flex w-full flex-col gap-3'>
                    <div className='bg-status-warning/20 border-status-warning flex items-start gap-3 rounded-xl border p-4'>
                        <LuTriangleAlert className='text-status-warning size-5 shrink-0' />
                        <div className='text-foreground-default text-sm font-medium'>
                            <FormattedMessage
                                id='platform.wizard.enter-tracking-number.already-exists'
                                description='Warning shown when the entered tracking number already belongs to an existing transaction'
                                defaultMessage='This tracking number is already in use. Review the existing transaction below or use a different tracking number before continuing.'
                            />
                        </div>
                    </div>

                    <div className='grid w-full grid-cols-1 gap-2'>
                        {viewProps.existingTransactions.map((transaction) => (
                            <ExistingTrackingNumberMatch
                                key={transaction.id}
                                transaction={transaction}
                                currentUserId={viewProps.currentUserId}
                                isContinuing={viewProps.continuingTransactionId === transaction.id}
                                onContinueWithTransaction={(existingTransaction) => {
                                    void viewProps.onContinueWithExistingTransaction(existingTransaction);
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </WizardScreenContainer>
    );
};

export default EnterTrackingNumber;
